// src/hooks/init/useAppStateGates.js
import { useState, useMemo, useCallback } from 'react';
import * as storage from '../../browserStorage';
import { consoleDebug } from '../../util';
import { isSweedTestMode } from '../../sweedConfig';

export const useAppStateGates = ({
    locationGateConfig,
    isSingleRetailer,
    locationSelected,
    retailerId,
    nonHeadlessRetailer,
    isInitialized,
}) => {
    const [ageGatePassed, setAgeGatePassed] = useState(() =>
        storage.getItem('HEADLESS_AGE_GATE_PASSED') === 'true'
    );

    const needsAgeGate = useMemo(() => {
        const needs = locationGateConfig?.enableAgeGate && !ageGatePassed && !nonHeadlessRetailer;
        consoleDebug('🔍 needsAgeGate check:', {
            enableAgeGate: locationGateConfig?.enableAgeGate,
            ageGatePassed,
            nonHeadlessRetailer: !!nonHeadlessRetailer,
            result: needs
        });
        return needs;
    }, [locationGateConfig, ageGatePassed, nonHeadlessRetailer]);

    const needsLocationGate = useMemo(() => {
        const needs = !isSingleRetailer && !locationSelected && !retailerId && !nonHeadlessRetailer;
        consoleDebug('🔍 needsLocationGate check:', {
            isSingleRetailer,
            locationSelected,
            retailerId,
            nonHeadlessRetailer: !!nonHeadlessRetailer,
            result: needs
        });
        return needs;
    }, [isSingleRetailer, locationSelected, retailerId, nonHeadlessRetailer]);

    const gatesPassed = useMemo(() => {
        // TEST HARNESS: skip the location/age gate so any page renders Sweed data.
        if (isSweedTestMode()) return true;

        const passed = (!needsLocationGate || locationSelected) &&
            (!needsAgeGate || ageGatePassed);
        consoleDebug('🔍 gatesPassed check:', {
            needsLocationGate,
            locationSelected,
            needsAgeGate,
            ageGatePassed,
            result: passed
        });
        return passed;
    }, [needsLocationGate, locationSelected, needsAgeGate, ageGatePassed]);

    const isAppReady = useMemo(() => {
        const ready = isInitialized && gatesPassed;
        consoleDebug('🔍 isAppReady check:', {
            isInitialized,
            gatesPassed,
            result: ready
        });
        return ready;
    }, [isInitialized, gatesPassed]);

    const passAgeGate = useCallback(() => {
        storage.setItem('HEADLESS_AGE_GATE_PASSED', 'true');
        setAgeGatePassed(true);
        consoleDebug('✅ Age gate passed');
    }, []);

    const resetGates = useCallback(() => {
        storage.removeItem('HEADLESS_AGE_GATE_PASSED');
        setAgeGatePassed(false);
        consoleDebug('🔄 Gates reset');
    }, []);

    return {
        ageGatePassed,
        needsAgeGate,
        needsLocationGate,
        gatesPassed,
        isAppReady,
        passAgeGate,
        resetGates,
    };
};
